/**
 * Pure color math — the single home for luminance / contrast / hex-rgb helpers
 * that had drifted into ~6 divergent copies across the app (button contrast,
 * banner text, poll pills, image-metadata). Mirrors PopshopRN's canonical
 * `src/utils/imageColorUtils.ts`, promoted to a cross-cutting util so
 * `@ps/features`, `@ps/ui`, and the `useImageAverageColor` hook share ONE
 * implementation. No React, no DOM — safe on the server and in the visitor bundle.
 */

export type Rgb = [number, number, number];

/**
 * Luminance model for the contrast picker.
 * - `'wcag'` — sRGB-gamma-corrected relative luminance (the canonical choice).
 * - `'perceived'` — the cheaper YIQ approximation.
 */
export type LuminanceModel = 'wcag' | 'perceived';

const clampByte = (value: number): number =>
  Math.max(0, Math.min(255, Math.round(value)));

/**
 * Parse `#rgb` / `#rrggbb` / `rgb(...)` / `rgba(...)` → `[r, g, b]`, or `null`
 * for anything unrecognized (named colors, gradients, empty).
 */
export function parseColorToRgb(color: string | undefined): Rgb | null {
  if (!color) return null;
  const value = color.trim();

  if (value.startsWith('#')) {
    const clean = value.slice(1);
    const full =
      clean.length === 3
        ? clean
            .split('')
            .map(channel => channel + channel)
            .join('')
        : clean;
    if (full.length !== 6) return null;
    const rgb: Rgb = [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16),
    ];
    return rgb.some(Number.isNaN) ? null : rgb;
  }

  if (value.toLowerCase().startsWith('rgb')) {
    const nums = value.match(/\d+/g);
    if (!nums || nums.length < 3) return null;
    return [Number(nums[0]), Number(nums[1]), Number(nums[2])];
  }

  return null;
}

/**
 * `#rrggbb` → `[r, g, b]`. Assumes a valid 7-char hex (caller has validated);
 * use {@link parseColorToRgb} for flexible / untrusted input.
 */
export function hexToRgb(hex: string): Rgb {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

/** `[r, g, b]` → `#rrggbb` (clamped to 0–255, rounded, zero-padded). */
export function rgbToHex(red: number, green: number, blue: number): string {
  return `#${[red, green, blue]
    .map(channel => clampByte(channel).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Perceived (YIQ) luminance in 0–1: `(0.299r + 0.587g + 0.114b) / 255`. */
export function perceivedLuminance(
  red: number,
  green: number,
  blue: number,
): number {
  return (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
}

/** WCAG relative luminance in 0–1 (sRGB gamma-corrected channels). */
export function relativeLuminance(
  red: number,
  green: number,
  blue: number,
): number {
  const linearize = (channel: number): number => {
    const srgb = channel / 255;
    return srgb <= 0.03928
      ? srgb / 12.92
      : Math.pow((srgb + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * linearize(red) +
    0.7152 * linearize(green) +
    0.0722 * linearize(blue)
  );
}

/** Per-model default light/dark cutoff (the thresholds the app shipped with). */
const DEFAULT_THRESHOLD: Record<LuminanceModel, number> = {
  wcag: 0.45,
  perceived: 0.5,
};

export interface ContrastOptions {
  /** Luminance model. Defaults to the canonical `'wcag'`. */
  model?: LuminanceModel;
  /** Light/dark cutoff (0–1). Defaults per model (wcag 0.45, perceived 0.5). */
  threshold?: number;
}

/** `'#000000'` or `'#ffffff'` for the given RGB, by luminance. Canonical: WCAG @ 0.45. */
export function contrastingTextColorFromRgb(
  [red, green, blue]: Rgb,
  { model = 'wcag', threshold }: ContrastOptions = {},
): '#000000' | '#ffffff' {
  const luminance =
    model === 'perceived'
      ? perceivedLuminance(red, green, blue)
      : relativeLuminance(red, green, blue);
  const cutoff = threshold ?? DEFAULT_THRESHOLD[model];
  return luminance > cutoff ? '#000000' : '#ffffff';
}

/**
 * `'#000000'` or `'#ffffff'` text that reads on `color` (`#hex` / `rgb()`).
 * Canonical WCAG @ 0.45 by default; pass `{ model: 'perceived' }` for the
 * cheaper YIQ variant. Unparseable input falls back to `'#000000'`.
 */
export function getContrastingTextColor(
  color: string,
  opts?: ContrastOptions,
): '#000000' | '#ffffff' {
  const rgb = parseColorToRgb(color);
  if (!rgb) return '#000000';
  return contrastingTextColorFromRgb(rgb, opts);
}

/** Shift a color's brightness by `amount` (-1..1, i.e. ±255·amount) → `rgb(...)`. */
export function adjustBrightness(color: string, amount: number): string {
  const rgb = parseColorToRgb(color);
  if (!rgb) return color;
  const shift = (channel: number): number => clampByte(channel + 255 * amount);
  return `rgb(${shift(rgb[0])}, ${shift(rgb[1])}, ${shift(rgb[2])})`;
}

/** `[r, g, b]` (0–255) → `[h, s, l]` (h 0–360, s/l 0–1). */
export function rgbToHsl(red: number, green: number, blue: number): Rgb {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  if (max === min) {
    return [0, 0, lightness];
  }
  const delta = max - min;
  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue: number;
  if (max === r) {
    hue = (g - b) / delta + (g < b ? 6 : 0);
  } else if (max === g) {
    hue = (b - r) / delta + 2;
  } else {
    hue = (r - g) / delta + 4;
  }
  return [hue * 60, saturation, lightness];
}

/** `[h, s, l]` (h 0–360, s/l 0–1) → `[r, g, b]` (0–255). */
export function hslToRgb(
  hue: number,
  saturation: number,
  lightness: number,
): Rgb {
  const h = (((hue % 360) + 360) % 360) / 360;
  if (saturation === 0) {
    const value = clampByte(lightness * 255);
    return [value, value, value];
  }
  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;
  const channel = (t0: number): number => {
    let t = t0;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    clampByte(channel(h + 1 / 3) * 255),
    clampByte(channel(h) * 255),
    clampByte(channel(h - 1 / 3) * 255),
  ];
}

/** WCAG relative luminosity (0–1) of a CSS color; `0` for unparseable input. */
export function colorLuminosity(color: string): number {
  const rgb = parseColorToRgb(color);
  return rgb ? relativeLuminance(rgb[0], rgb[1], rgb[2]) : 0;
}

/**
 * Lighten a color by scaling its HSL lightness up by `ratio` (relative) —
 * `l → min(1, l · (1 + ratio))`. Matches the `color` npm lib's `lighten`, which
 * PopshopRN uses for poll pills. Returns `#rrggbb`; unparseable input unchanged.
 */
export function lighten(color: string, ratio: number): string {
  const rgb = parseColorToRgb(color);
  if (!rgb) return color;
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const [r, g, b] = hslToRgb(h, s, Math.min(1, l * (1 + ratio)));
  return rgbToHex(r, g, b);
}

/**
 * Darken a color by scaling its HSL lightness down by `ratio` (relative) —
 * `l → max(0, l · (1 - ratio))`. Matches the `color` npm lib's `darken`.
 */
export function darken(color: string, ratio: number): string {
  const rgb = parseColorToRgb(color);
  if (!rgb) return color;
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const [r, g, b] = hslToRgb(h, s, Math.max(0, l * (1 - ratio)));
  return rgbToHex(r, g, b);
}
